import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import type {
  OrgMember,
  AddOrgMemberRequest,
  AddOrgMemberResponse,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

const extractErrorMessage = (error: any): string => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0].msg || 'An error occurred';
    }
  }
  return 'An error occurred';
};

interface OrgMembershipListResponse {
  items: OrgMember[];
  total: number;
  skip: number;
  limit: number;
}

export const useListOrgMembers = (unitId: string | null, skip: number = 0, limit: number = 20) => {
  return useQuery({
    queryKey: ['orgUnits', unitId, 'members', skip, limit],
    queryFn: async () => {
      if (!unitId) return { items: [], total: 0, skip, limit };
      const { data } = await apiClient.get<OrgMembershipListResponse>(
        `/org/units/${unitId}/members?skip=${skip}&limit=${limit}`
      );
      return data;
    },
    enabled: !!unitId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useAddOrgMember = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: {
      unitId: string;
      request: AddOrgMemberRequest;
    }) => {
      const { data } = await apiClient.post<AddOrgMemberResponse>(
        `/org/units/${payload.unitId}/members`,
        payload.request
      );
      return data;
    },
    onSuccess: (data, variables) => {
      addNotification({
        message: 'Member added successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: ['orgUnits', variables.unitId, 'members'],
      });
      queryClient.invalidateQueries({
        queryKey: ['orgUnits', variables.unitId],
      });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to add member',
        type: 'error',
      });
    },
  });
};

export const useRemoveOrgMember = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: {
      unitId: string;
      memberId: string;
    }) => {
      await apiClient.delete(
        `/org/units/${payload.unitId}/members/${payload.memberId}`
      );
      return payload;
    },
    onSuccess: (data) => {
      addNotification({
        message: 'Member removed successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: ['orgUnits', data.unitId, 'members'],
      });
      queryClient.invalidateQueries({
        queryKey: ['orgUnits', data.unitId],
      });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to remove member',
        type: 'error',
      });
    },
  });
};
